import {
  ConsultProposalResponseDTO,
  ConsultProposalSituationResponseDTO,
  TIPO_CORRESPONDENCIA,
  TIPO_OPCAO_PAGAMENTO,
  TIPO_PESSOA,
  TIPO_RESIDENCIA
} from "@repo/core/services/entities/Proposals";
import {
  PROPOSTA_SITUACAO,
  CreateProposalInputDTO,
  UpdateProposalInputDTO
} from "@repo/core/services/entities/Proposals";
import { pickFirstValidErpDate } from "@repo/core/utils/erpDate.utils";
import { getCleanCPF, removeAccents } from "@repo/core/utils/string.utils";
import { normalizeResidentialPhone2TipoToApiCode } from "@repo/core/utils/residentialPhone2.utils";
import { InfoSecurityFormDataSchemaType } from "@repo/core/stores/proposalForm/types";
import {
  GetClientInfoResponseDTO,
  LstCartao
} from "@repo/core/services/entities/Client";
import type { WayupProposalDataResponseDTO } from "@repo/core/services/entities/WayupRequisitions";
import { PreProposalData } from "../../store/preProposalStore";
import { DraftProposalData } from "./types";
import { LimitReassessmentEnum } from "@repo/core/enums/proposals";
import { ProposalTypeByCode } from "@repo/core/constants/proposals";

/**
 * Field sources for the reanalysis flow.
 *
 * Endpoints:
 * - `clientInfo`      → `/Cliente/ConsultarDadosCadastrais`
 * - `proposalDetails` → `/PropostaMobile/ConsultarProposta`
 * - `firstCardInfo`   → `/Cliente/ConsultarPerfilPropostas` (lstCartoes[0])
 *
 * Each resolver picks the primary source defined in the spec table and falls
 * back to the other endpoints when the primary value is empty, so the
 * Proposta/Incluir payload (and the store snapshot that mirrors it) never
 * loses data that exists elsewhere.
 */
interface ReanalysisSources {
  clientInfo: GetClientInfoResponseDTO;
  proposalDetails?: ConsultProposalResponseDTO;
  firstCardInfo?: LstCartao;
}

type ProposalDetailsWithStoreAliases = Partial<ConsultProposalResponseDTO> &
  Partial<
    Pick<
      PreProposalData,
      | "strEndDdd2"
      | "strEndFone2"
      | "strEndTipoFone2"
      | "strEndMesAno"
      | "strCorrespondencia"
      | "strOpcaoPagamentos"
      | "strAtivSetor"
    >
  > & {
    strUF?: string;
    restipofone2?: string;
  };

const pickFirstNonEmptyString = (
  ...values: Array<string | undefined | null>
): string | undefined => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return undefined;
};

const pickFirstDefinedNumber = (
  ...values: Array<number | undefined | null>
): number | undefined => {
  for (const value of values) {
    if (value !== undefined && value !== null && !Number.isNaN(value)) {
      return value;
    }
  }
  return undefined;
};

const pickFirstPositiveNumber = (
  ...values: Array<number | undefined | null>
): number | undefined => {
  for (const value of values) {
    if (
      value !== undefined &&
      value !== null &&
      !Number.isNaN(value) &&
      value > 0
    ) {
      return value;
    }
  }
  return undefined;
};

const hasProposalValue = (value: unknown): boolean => {
  if (value === undefined || value === null) {
    return false;
  }
  if (typeof value === "string") {
    return value.trim().length > 0;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0;
  }
  return true;
};

const mergeMissingProposalData = (
  proposalData: Partial<PreProposalData>,
  fallbackData: Partial<PreProposalData>
): Partial<PreProposalData> => {
  const merged = { ...proposalData } as Record<keyof PreProposalData, unknown>;

  for (const [key, value] of Object.entries(fallbackData) as Array<
    [keyof PreProposalData, unknown]
  >) {
    if (!hasProposalValue(merged[key]) && hasProposalValue(value)) {
      merged[key] = value;
    }
  }

  return merged as Partial<PreProposalData>;
};

const resolveNome = ({
  clientInfo,
  proposalDetails,
  firstCardInfo
}: ReanalysisSources): string | undefined =>
  pickFirstNonEmptyString(
    firstCardInfo?.strNome,
    clientInfo.strNome,
    proposalDetails?.strNome
  );

const resolveNomeMae = ({
  clientInfo,
  proposalDetails
}: ReanalysisSources): string | undefined =>
  pickFirstNonEmptyString(proposalDetails?.strNomeMae, clientInfo.strNomeMae);

const resolveSexo = ({
  clientInfo,
  proposalDetails
}: ReanalysisSources): string | undefined =>
  pickFirstNonEmptyString(proposalDetails?.strSexo, clientInfo.strSexo);

const resolveEstadoCivil = ({
  clientInfo,
  proposalDetails
}: ReanalysisSources): string | undefined =>
  pickFirstNonEmptyString(
    proposalDetails?.strEstadoCivil,
    clientInfo.strEstCivil
  );

const resolveNascimento = ({
  clientInfo,
  proposalDetails,
  firstCardInfo
}: ReanalysisSources): string | undefined =>
  pickFirstNonEmptyString(
    firstCardInfo?.datDataNascimento,
    clientInfo.datDataNascimento,
    proposalDetails?.datDataNascimento
  );

const resolveNroDocIdent = ({
  clientInfo,
  proposalDetails
}: ReanalysisSources): string | undefined =>
  pickFirstNonEmptyString(
    proposalDetails?.strNumeroDocumento,
    clientInfo.strNroDocIdent
  );

const resolveEmail = ({
  clientInfo,
  proposalDetails,
  firstCardInfo
}: ReanalysisSources): string | undefined =>
  pickFirstNonEmptyString(
    firstCardInfo?.strEmail,
    clientInfo.strEmail,
    proposalDetails?.strEmail
  );

const resolveClasseProfissional = ({
  clientInfo,
  proposalDetails
}: ReanalysisSources): string | undefined =>
  pickFirstNonEmptyString(
    proposalDetails?.strClasseProfissional,
    clientInfo.strClasseProfissional
  );

const resolveAtivSetor = ({
  clientInfo,
  proposalDetails
}: ReanalysisSources): string | undefined =>
  pickFirstNonEmptyString(
    proposalDetails?.strProfissao,
    clientInfo.strAtivSetor
  );

const resolveAtivDataAdmissao = ({
  proposalDetails
}: ReanalysisSources): string | undefined =>
  pickFirstValidErpDate(proposalDetails?.strTrabalhaDesde);

const resolveRenda = ({
  proposalDetails
}: ReanalysisSources): number | undefined =>
  pickFirstPositiveNumber(proposalDetails?.dblRenda);

const resolveDiaCartao = ({
  proposalDetails,
  firstCardInfo
}: ReanalysisSources): number | undefined =>
  pickFirstDefinedNumber(
    firstCardInfo?.intDiaCartao,
    proposalDetails?.intDiaVencimento
  );

const resolveRefPessRel1 = ({
  clientInfo,
  proposalDetails
}: ReanalysisSources): string | undefined =>
  pickFirstNonEmptyString(
    proposalDetails?.strRefPessRel1,
    clientInfo.strRefPessRel1
  );

const resolveEnvioFatura = ({
  proposalDetails
}: ReanalysisSources): string | undefined =>
  pickFirstNonEmptyString(proposalDetails?.strEnvioFatura);

export const mapClientAdditionalPhoneToProposalData = (
  clientInfo: Partial<GetClientInfoResponseDTO>
): Partial<PreProposalData> => {
  const ddd = pickFirstNonEmptyString(clientInfo.strResDDD2);
  const phone = pickFirstNonEmptyString(clientInfo.strResFone2);
  const phoneType = pickFirstNonEmptyString(clientInfo.strResTipoFone2);

  return {
    ...(ddd ? { strEndDdd2: ddd, strResDDD2: ddd } : {}),
    ...(phone ? { strEndFone2: phone, strResFone2: phone } : {}),
    ...(phoneType
      ? {
          strEndTipoFone2: normalizeResidentialPhone2TipoToApiCode(phoneType),
          strResTipoFone2: normalizeResidentialPhone2TipoToApiCode(phoneType)
        }
      : {})
  };
};

export const mapWayupProposalPhoneTypeToProposalData = (
  proposal: WayupProposalDataResponseDTO
): Partial<PreProposalData> => {
  const phoneType = pickFirstNonEmptyString(proposal.restipofone2);

  return phoneType
    ? {
        strEndTipoFone2: normalizeResidentialPhone2TipoToApiCode(phoneType),
        strResTipoFone2: normalizeResidentialPhone2TipoToApiCode(phoneType)
      }
    : {};
};

const mapProposalAdditionalPhoneToProposalData = (
  proposal: ProposalDetailsWithStoreAliases
): Partial<PreProposalData> => {
  const ddd = pickFirstNonEmptyString(
    proposal.strEndDdd2,
    proposal.strResDDD2,
    proposal.strTelefoneAdicionalDDD
  );
  const phone = pickFirstNonEmptyString(
    proposal.strEndFone2,
    proposal.strResFone2,
    proposal.strTelefoneAdicional
  );
  const phoneType = pickFirstNonEmptyString(
    proposal.strEndTipoFone2,
    proposal.strResTipoFone2,
    proposal.restipofone2
  );

  return {
    ...(ddd ? { strEndDdd2: ddd, strResDDD2: ddd } : {}),
    ...(phone ? { strEndFone2: phone, strResFone2: phone } : {}),
    ...(phoneType
      ? {
          strEndTipoFone2: normalizeResidentialPhone2TipoToApiCode(phoneType),
          strResTipoFone2: normalizeResidentialPhone2TipoToApiCode(phoneType)
        }
      : {})
  };
};

const mapClientInfoToProposalEditFallbackData = (
  clientInfo: Partial<GetClientInfoResponseDTO>
): Partial<PreProposalData> => {
  const admissionDate = pickFirstValidErpDate(clientInfo.strAtivDataAdmissao);
  const incomeAmount = pickFirstPositiveNumber(clientInfo.dblAtivRenda);
  const professionCode = pickFirstNonEmptyString(clientInfo.strAtivSetor);
  const email = pickFirstNonEmptyString(clientInfo.strEmail);

  return {
    strNome: clientInfo.strNome,
    strCliente: clientInfo.strCpf,
    strEndDdd1: clientInfo.strResDDD,
    strEndFone1: clientInfo.strResFone,
    datNascimento: clientInfo.datDataNascimento,
    strNomeMae: clientInfo.strNomeMae,
    strNomePai: clientInfo.strNomePai,
    strSexo: clientInfo.strSexo,
    strEstadoCivil: clientInfo.strEstCivil,
    strNroDocIdent: clientInfo.strNroDocIdent,
    strTipoDocIdent: clientInfo.strTipoDocIdent,
    strUfEmiDocIdent: clientInfo.strUfEmiDocIdent,
    strOrgaoEmissor: clientInfo.strOrgaoEmissor,
    strDataEmiDocIdent: clientInfo.strDataEmiDocIdent,
    strEmail: email,
    strInfoEmail: email,
    strResCep: clientInfo.strResCEP,
    strResEndereco: clientInfo.strResEndereco,
    strResNumero: clientInfo.strResNumero,
    strResBairro: clientInfo.strResBairro,
    strResCidade: clientInfo.strResCidade,
    strResUF: clientInfo.strResUF,
    strResComplemento: clientInfo.strResComplemento,
    strAtivEmpresa: clientInfo.strAtivEmpresa,
    strAtivCargo: clientInfo.strAtivCargo,
    strAtivSetor: professionCode,
    strProfissao: professionCode,
    strAtivProfissao: professionCode,
    strAtivDataAdmissao: admissionDate,
    datTrabDesde: admissionDate,
    datAtivDataAdmissao: admissionDate,
    strRenda: incomeAmount,
    dblRenda: incomeAmount,
    dblComSalario: incomeAmount,
    strClasseProfissional: clientInfo.strClasseProfissional,
    strEndTipoResidencia: clientInfo.strResTipoResidencia,
    strEndMesAno: clientInfo.strResMesAno,
    strRefPessNome1: clientInfo.strRefPessNome1,
    strRefPessRel1: clientInfo.strRefPessRel1,
    strRefPessFone1: clientInfo.strRefPessFone1,
    strRefPessDdd1: clientInfo.strRefPessDdd1,
    strCorrespondencia: clientInfo.strCorrespondencia,
    ...mapClientAdditionalPhoneToProposalData(clientInfo)
  };
};

export const mergeProposalDataWithClientInfoFallback = (
  proposalData: Partial<PreProposalData>,
  clientInfo: Partial<GetClientInfoResponseDTO>
): Partial<PreProposalData> =>
  mergeMissingProposalData(
    proposalData,
    mapClientInfoToProposalEditFallbackData(clientInfo)
  );

export const applyProposalReassessmentFromSituation = (
  proposalData: Partial<PreProposalData>,
  situation: Pick<ConsultProposalSituationResponseDTO, "strReavaliacaoLimite">
): Partial<PreProposalData> => ({
  ...proposalData,
  strReavaliacaoLimite:
    situation.strReavaliacaoLimite?.trim() || proposalData.strReavaliacaoLimite
});

/**
 * Maps proposal response to proposal data structure
 */
export const mapProposalToProposalData = (
  proposal: ConsultProposalResponseDTO
): Partial<PreProposalData> => {
  const proposalWithAliases = proposal as ProposalDetailsWithStoreAliases;
  const admissionDate = pickFirstValidErpDate(
    proposal.datAtivDataAdmissao,
    proposal.datTrabDesde,
    proposal.strTrabalhaDesde
  );
  const incomeAmount = pickFirstPositiveNumber(proposal.dblRenda);
  const email = pickFirstNonEmptyString(
    proposal.strInfoEmail,
    proposal.strEmail
  );
  const professionCode = pickFirstNonEmptyString(
    proposal.strProfissao,
    proposal.strAtivProfissao,
    proposalWithAliases.strAtivSetor
  );
  const residenceType = pickFirstNonEmptyString(
    proposal.strEndTipoResidencia,
    proposal.strResTipoResidencia
  );
  const residenceSince = pickFirstNonEmptyString(
    proposalWithAliases.strEndMesAno,
    proposal.strResMesAno
  );
  const correspondence = pickFirstNonEmptyString(
    proposalWithAliases.strCorrespondencia,
    proposal.strEnvioFatura
  );

  return {
    lngProposta: proposal.lngProposta,
    strNome: proposal.strNome,
    strCliente: proposal.strCpf || proposal.strCliente,
    strEndDdd1: proposal.strCelularDDD,
    strEndFone1: proposal.strCelular,
    strProduto: proposal.strProduto,
    strBandeira: proposal.strBandeira,
    strPontoAtendimento: proposal.strPontoAtendimento,
    strAtendente: proposal.strAtendente,
    strSituacao: proposal.strSituacao as PROPOSTA_SITUACAO,
    datNascimento: proposal.datDataNascimento,
    strNomeMae: proposal.strNomeMae,
    strSexo: proposal.strSexo,
    strEstadoCivil: proposal.strEstadoCivil,
    strNroDocIdent: proposal.strNroDocIdent,
    strTipoDocIdent: proposal.strTipoDocumento,
    strEmail: email,
    strInfoEmail: email,
    strResCep: pickFirstNonEmptyString(proposal.strResCep, proposal.strCep),
    strResEndereco: pickFirstNonEmptyString(
      proposal.strResEndereco,
      proposal.strEndereco
    ),
    strResNumero: pickFirstNonEmptyString(
      proposal.strResNumero,
      proposal.strNumero
    ),
    strResBairro: pickFirstNonEmptyString(
      proposal.strResBairro,
      proposal.strBairro
    ),
    strResCidade: pickFirstNonEmptyString(
      proposal.strResCidade,
      proposal.strCidade
    ),
    strResUF: pickFirstNonEmptyString(
      proposal.strResUF,
      proposal.strUf,
      proposalWithAliases.strUF
    ),
    strResComplemento: pickFirstNonEmptyString(
      proposal.strResComplemento,
      proposal.strComplemento
    ),
    strClasseProfissional: proposal.strClasseProfissional,
    strAtivSetor: professionCode,
    strProfissao: professionCode,
    strAtivProfissao: professionCode,
    strTrabalhaDesde: admissionDate,
    strAtivDataAdmissao: admissionDate,
    datAtivDataAdmissao: admissionDate,
    datTrabDesde: admissionDate,
    strRenda: incomeAmount,
    dblRenda: incomeAmount,
    dblComSalario: pickFirstPositiveNumber(
      proposal.dblComSalario,
      incomeAmount
    ),
    strEndTipoResidencia: residenceType,
    strEndMesAno: residenceSince,
    strRefPessNome1: proposal.strRefPessNome1,
    strRefPessRel1: proposal.strRefPessRel1,
    strRefPessFone1: proposal.strRefPessFone1,
    strRefPessDdd1: proposal.strRefPessDdd1,
    strCorrespondencia: correspondence,
    strEnvioFatura: proposal.strEnvioFatura,
    intDiaCartao: pickFirstDefinedNumber(
      proposal.intDiaCartao,
      proposal.intDiaVencimento
    ),
    strOpcaoPagamentos: pickFirstNonEmptyString(
      proposalWithAliases.strOpcaoPagamentos,
      proposal.strOpcaoPagamento
    ),
    strReavaliacaoLimite: proposal.strReavaliacaoLimite,
    strTipoProposta: proposal.strTipoProposta || ProposalTypeByCode.AMBOS,
    ...mapProposalAdditionalPhoneToProposalData(proposalWithAliases),

    // Fixed fields
    strTipoPessoa:
      (proposal.strTipoPessoa as TIPO_PESSOA) || TIPO_PESSOA.FISICA,
    strEndTipoFone1: TIPO_RESIDENCIA.RESIDENCIAL,
    strResTipoFone1: TIPO_RESIDENCIA.RESIDENCIAL
  };
};

/**
 * Maps proposal to proposal data while preserving flow context fields
 * (strBandeira, strPontoAtendimento, strAtendente, strProduto, strReavaliacaoLimite)
 * from current store. API data takes priority for all other fields
 * (phone, lngProposta, etc.) to ensure the store reflects the latest
 * proposal details when loading a draft.
 */
export const mapProposalToProposalDataPreservingFlowContext = (
  proposal: ConsultProposalResponseDTO,
  currentProposalData: PreProposalData | undefined
): Partial<PreProposalData> => {
  const mapped = mapProposalToProposalData(proposal);

  const contextChanged =
    (currentProposalData?.strAtendente &&
      currentProposalData.strAtendente !== mapped.strAtendente) ||
    (currentProposalData?.strPontoAtendimento &&
      currentProposalData.strPontoAtendimento !== mapped.strPontoAtendimento);

  return {
    ...mapped,
    // Preserve only flow context fields from the current store
    strBandeira: currentProposalData?.strBandeira || mapped.strBandeira,
    strPontoAtendimento:
      currentProposalData?.strPontoAtendimento || mapped.strPontoAtendimento,
    strAtendente: currentProposalData?.strAtendente || mapped.strAtendente,
    strProduto: currentProposalData?.strProduto || mapped.strProduto,
    strReavaliacaoLimite:
      currentProposalData?.strReavaliacaoLimite || mapped.strReavaliacaoLimite,
    needsContextUpdate: !!contextChanged
  };
};

/**
 * Builds payload for Proposta/Alterar when attendant or service point changed
 * relative to the original proposal (e.g. draft continued by a different user).
 * Sends the FULL store snapshot so no data is lost during the context update.
 */
export const mapToContextUpdateInput = (
  proposalData: PreProposalData,
  formData: { cleanCPF: string; fullName: string }
): UpdateProposalInputDTO => {
  /* eslint-disable @typescript-eslint/no-unused-vars -- strip local-only store fields */
  const {
    uploadedProposalDocumentTypes,
    unicoStatus,
    shouldForceManualCapture,
    restartRequired,
    shouldForceNewBiometry,
    biometryReuseProcessId,
    needsContextUpdate,
    unicoUploadedIdentityDocumentTypes,
    manualCaptureExcludedDocumentTypes,
    queueEditMode,
    lngProposta,
    blnExisteRegistroUnico,
    strNomeDocumento,
    strNomeArquivoBiometria,
    strArquivoBiometria,
    strBase64Biometria,
    strIdProcessoUnico,
    intScore,
    datAtivDataAdmissao: rawDatAtivDataAdmissao,
    strAtivDataAdmissao,
    datTrabDesde,
    strTrabalhaDesde,
    ...apiFields
  } = proposalData;
  /* eslint-enable @typescript-eslint/no-unused-vars */

  const admissionDate = pickFirstValidErpDate(
    rawDatAtivDataAdmissao,
    strAtivDataAdmissao,
    datTrabDesde,
    strTrabalhaDesde
  );

  return {
    ...apiFields,
    lngProposta: String(lngProposta),
    strNome: formData.fullName,
    strCliente: formData.cleanCPF,
    strSituacao: proposalData.strSituacao || PROPOSTA_SITUACAO.DATA_CONFERENCE,
    blnManterPendente: true,
    strTipoPessoa: TIPO_PESSOA.FISICA,
    strEndTipoFone1: TIPO_RESIDENCIA.RESIDENCIAL,
    strResTipoFone1: TIPO_RESIDENCIA.RESIDENCIAL,
    strTipoProposta: proposalData.strTipoProposta || ProposalTypeByCode.AMBOS,
    ...(admissionDate ? { datAtivDataAdmissao: admissionDate } : {}),
    strTipoCorrespondencia:
      (proposalData.strTipoCorrespondencia as
        | TIPO_CORRESPONDENCIA
        | undefined) || TIPO_CORRESPONDENCIA.RESIDENCIAL
  };
};

/**
 * Extracts form data from InfoSecurityFormDataSchemaType (CPF and fullName only).
 * Removes accents from fullName before sending to backend (e.g. "Célio" -> "Celio").
 */
export const extractInfoSecurityFormData = (
  formData: InfoSecurityFormDataSchemaType
): { cleanCPF: string; fullName: string } => {
  const cleanCPF = getCleanCPF(formData.cpf);
  const fullNameWithoutAccents = removeAccents(formData.fullName.trim());

  return {
    cleanCPF,
    fullName: fullNameWithoutAccents
  };
};

/**
 * Maps draft proposal to form data structure
 */
export const mapDraftToFormData = (
  proposal: ConsultProposalResponseDTO,
  fallbackCPF: string
): DraftProposalData => {
  const cleanCPFValue = proposal.strCpf || proposal.strCliente || fallbackCPF;
  const phoneDDD = proposal.strCelularDDD || "";
  const phone = proposal.strCelular || "";

  return {
    proposal,
    proposalId: proposal.lngProposta?.toString() || "",
    phoneDDD,
    phone,
    cleanCPF: cleanCPFValue,
    fullName: proposal.strNome || ""
  };
};

/**
 * Maps client info and proposal data to CreateProposalInputDTO for reanalysis proposal creation.
 *
 * Field source rule (spec table): each field has a primary endpoint and falls
 * back to the other two endpoints when the primary value is empty.
 *
 *  - NOME            → form input > ConsultarPerfilPropostas > ConsultarDadosCadastrais > ConsultarProposta
 *  - MAE             → ConsultarProposta > ConsultarDadosCadastrais
 *  - ESTCIVIL        → ConsultarProposta > ConsultarDadosCadastrais
 *  - SEXO            → ConsultarProposta > ConsultarDadosCadastrais
 *  - NASCIMENTO      → ConsultarPerfilPropostas > ConsultarDadosCadastrais > ConsultarProposta
 *  - NRODOCIDENT     → ConsultarProposta (strNumeroDocumento) > ConsultarDadosCadastrais
 *  - INFOEMAIL       → ConsultarPerfilPropostas > ConsultarDadosCadastrais > ConsultarProposta
 *  - CLASSEPROFISSIONAL → ConsultarProposta > ConsultarDadosCadastrais
 *  - ATIVSETOR       → ConsultarProposta (strProfissao) > ConsultarDadosCadastrais
 *  - ATIVDATAADMISSAO → ConsultarProposta (strTrabalhaDesde)
 *  - ATIVRENDA       → ConsultarProposta (dblRenda)
 *  - Address / RES* fields → ConsultarDadosCadastrais
 */
export const mapClientInfoToReanalysisProposalInput = (
  clientInfo: GetClientInfoResponseDTO,
  cleanCPF: string,
  proposalData: PreProposalData | undefined,
  proposalDetails?: ConsultProposalResponseDTO,
  /** Name as typed on Info Security (accent-stripped); takes priority over API client name */
  fullNameFromForm?: string,
  /** First active card from /Cliente/ConsultarPerfilPropostas (lstCartoes[0]) */
  firstCardInfo?: LstCartao
): CreateProposalInputDTO => {
  const sources: ReanalysisSources = {
    clientInfo,
    proposalDetails,
    firstCardInfo
  };

  const resolvedNome = fullNameFromForm || resolveNome(sources) || "";
  const resolvedEmail = resolveEmail(sources);
  const professionCode = resolveAtivSetor(sources);
  const admissionDate = resolveAtivDataAdmissao(sources);
  const incomeAmount = resolveRenda(sources);

  const proposalBase: Partial<CreateProposalInputDTO> = proposalDetails
    ? {
        strEndTipoResidencia: proposalDetails.strEndTipoResidencia,
        strEndMesAno: proposalDetails.strResMesAno,
        strRefPessNome1: proposalDetails.strRefPessNome1,
        strRefPessRel1: proposalDetails.strRefPessRel1,
        strRefPessFone1: proposalDetails.strRefPessFone1,
        strRefPessDdd1: proposalDetails.strRefPessDdd1,
        strCorrespondencia: proposalDetails.strEnvioFatura
      }
    : {};

  return {
    ...proposalBase,
    strSituacao: PROPOSTA_SITUACAO.DATA_CONFERENCE,
    strNome: resolvedNome,
    strCliente: cleanCPF,
    strAtendente: proposalData?.strAtendente || "",
    strProduto: proposalData?.strProduto || "",
    strPontoAtendimento: proposalData?.strPontoAtendimento || "",
    strBandeira: proposalData?.strBandeira,
    blnManterPendente: true,
    datNascimento: resolveNascimento(sources),
    strNomeMae: resolveNomeMae(sources),
    strSexo: resolveSexo(sources),
    strEstCivil: resolveEstadoCivil(sources),
    strEmail: resolvedEmail,
    strResCep: clientInfo.strResCEP,
    strResEndereco: clientInfo.strResEndereco,
    strResNumero: clientInfo.strResNumero,
    strResBairro: clientInfo.strResBairro,
    strResCidade: clientInfo.strResCidade,
    strResUF: clientInfo.strResUF,
    strResComplemento: clientInfo.strResComplemento,
    strNroDocIdent: resolveNroDocIdent(sources),
    strTipoDocIdent: clientInfo.strTipoDocIdent,
    strUfEmiDocIdent: clientInfo.strUfEmiDocIdent,
    strOrgaoEmissor: clientInfo.strOrgaoEmissor,
    strDataEmiDocIdent: clientInfo.strDataEmiDocIdent,
    strAtivEmpresa: clientInfo.strAtivEmpresa,
    strAtivCargo: clientInfo.strAtivCargo,
    strAtivSetor: professionCode,
    strAtivDataAdmissao: admissionDate,
    datTrabDesde: admissionDate,
    datAtivDataAdmissao: admissionDate,
    strRenda: incomeAmount,
    dblRenda: incomeAmount,
    dblComSalario: incomeAmount,
    strClasseProfissional: resolveClasseProfissional(sources),
    strEndTipoResidencia:
      clientInfo.strResTipoResidencia || proposalDetails?.strEndTipoResidencia,
    strEndMesAno: clientInfo.strResMesAno || proposalDetails?.strResMesAno,
    strRefPessNome1:
      proposalDetails?.strRefPessNome1 || clientInfo.strRefPessNome1,
    strRefPessRel1: resolveRefPessRel1(sources),
    strRefPessFone1:
      proposalDetails?.strRefPessFone1 || clientInfo.strRefPessFone1,
    strRefPessDdd1:
      proposalDetails?.strRefPessDdd1 || clientInfo.strRefPessDdd1,
    strCorrespondencia:
      clientInfo.strCorrespondencia || proposalDetails?.strEnvioFatura,
    strReavaliacaoLimite: LimitReassessmentEnum.REANALYSIS,
    intDiaCartao: resolveDiaCartao(sources),
    strEnvioFatura: resolveEnvioFatura(sources),

    strTipoPessoa: TIPO_PESSOA.FISICA,
    strEndTipoFone1: TIPO_RESIDENCIA.RESIDENCIAL,
    strResTipoFone1: TIPO_RESIDENCIA.RESIDENCIAL,
    strTipoProposta: ProposalTypeByCode.AMBOS,
    strOpcaoPagamentos: TIPO_OPCAO_PAGAMENTO.BANCO,
    strTipoCorrespondencia: TIPO_CORRESPONDENCIA.RESIDENCIAL,
    strInfoEmail: resolvedEmail,
    ...mapClientAdditionalPhoneToProposalData(clientInfo)
  };
};

/**
 * Maps client info to proposal data structure after reanalysis proposal creation.
 * Stores ALL fields that will be sent on subsequent Proposta/Alterar calls so
 * the auto-sync always mirrors the original Proposta/Incluir payload.
 *
 * Uses the same primary-source-with-fallback rule documented on
 * `mapClientInfoToReanalysisProposalInput`, so the store snapshot never
 * diverges from the Proposta/Incluir payload.
 *
 * Phone fields prefer the previous proposal's mobile (strCelularDDD/strCelular)
 * so "keep number" matches the number shown from the prior proposal;
 * residential phone from client info is used as fallback.
 */
export const mapClientInfoToProposalData = (
  clientInfo: GetClientInfoResponseDTO,
  cleanCPF: string,
  proposalId: number,
  previousProposal?: ConsultProposalResponseDTO,
  /** Name as typed on Info Security (accent-stripped); takes priority over API client name */
  fullNameFromForm?: string,
  /** First active card from /Cliente/ConsultarPerfilPropostas (lstCartoes[0]) */
  firstCardInfo?: LstCartao
): Partial<PreProposalData> => {
  const sources: ReanalysisSources = {
    clientInfo,
    proposalDetails: previousProposal,
    firstCardInfo
  };

  const dddFromProposal = previousProposal?.strCelularDDD?.trim();
  const phoneFromProposal = previousProposal?.strCelular?.trim();

  const resolvedNome = fullNameFromForm || resolveNome(sources) || "";
  const resolvedEmail = resolveEmail(sources);
  const professionCode = resolveAtivSetor(sources);
  const admissionDate = resolveAtivDataAdmissao(sources);
  const incomeAmount = resolveRenda(sources);

  return {
    lngProposta: proposalId,
    strNome: resolvedNome,
    strCliente: cleanCPF,
    strSituacao: PROPOSTA_SITUACAO.DATA_CONFERENCE,
    // Phone (mobile preferred, residential as fallback)
    strEndDdd1: dddFromProposal || clientInfo.strResDDD || "",
    strEndFone1: phoneFromProposal || clientInfo.strResFone || "",
    // Personal
    datNascimento: resolveNascimento(sources),
    strNomeMae: resolveNomeMae(sources),
    strNomePai: clientInfo.strNomePai,
    strSexo: resolveSexo(sources),
    strEstadoCivil: resolveEstadoCivil(sources),
    strNroDocIdent: resolveNroDocIdent(sources),
    strTipoDocIdent: clientInfo.strTipoDocIdent,
    strUfEmiDocIdent: clientInfo.strUfEmiDocIdent,
    strOrgaoEmissor: clientInfo.strOrgaoEmissor,
    strDataEmiDocIdent: clientInfo.strDataEmiDocIdent,
    strEmail: resolvedEmail,
    strInfoEmail: resolvedEmail,
    // Residential address
    strResCep: clientInfo.strResCEP,
    strResEndereco: clientInfo.strResEndereco,
    strResNumero: clientInfo.strResNumero,
    strResBairro: clientInfo.strResBairro,
    strResCidade: clientInfo.strResCidade,
    strResUF: clientInfo.strResUF,
    strResComplemento: clientInfo.strResComplemento,
    ...mapClientAdditionalPhoneToProposalData(clientInfo),
    // Employment
    strAtivEmpresa: clientInfo.strAtivEmpresa,
    strAtivCargo: clientInfo.strAtivCargo,
    strAtivSetor: professionCode,
    strProfissao: professionCode,
    strAtivProfissao: professionCode,
    strAtivDataAdmissao: admissionDate,
    datAtivDataAdmissao: admissionDate,
    datTrabDesde: admissionDate,
    strRenda: incomeAmount,
    dblRenda: incomeAmount,
    dblComSalario: incomeAmount,
    strClasseProfissional: resolveClasseProfissional(sources),
    strEndTipoResidencia:
      clientInfo.strResTipoResidencia || previousProposal?.strEndTipoResidencia,
    strEndMesAno: clientInfo.strResMesAno || previousProposal?.strResMesAno,
    strRefPessNome1:
      previousProposal?.strRefPessNome1 || clientInfo.strRefPessNome1,
    strRefPessRel1: resolveRefPessRel1(sources),
    strRefPessFone1:
      previousProposal?.strRefPessFone1 || clientInfo.strRefPessFone1,
    strRefPessDdd1:
      previousProposal?.strRefPessDdd1 || clientInfo.strRefPessDdd1,
    strCorrespondencia:
      clientInfo.strCorrespondencia || previousProposal?.strEnvioFatura,
    strTipoPessoa: TIPO_PESSOA.FISICA,
    strEndTipoFone1: TIPO_RESIDENCIA.RESIDENCIAL,
    strResTipoFone1: TIPO_RESIDENCIA.RESIDENCIAL,
    strTipoProposta: ProposalTypeByCode.AMBOS,
    strOpcaoPagamentos: TIPO_OPCAO_PAGAMENTO.BANCO,
    strTipoCorrespondencia: TIPO_CORRESPONDENCIA.RESIDENCIAL,
    strReavaliacaoLimite: LimitReassessmentEnum.REANALYSIS,
    intDiaCartao: resolveDiaCartao(sources),
    strEnvioFatura: resolveEnvioFatura(sources),
    // Clear stale context flag so handleKeepNumber does not fire a second alterar
    needsContextUpdate: false
  };
};
